import { ReactNode, useState } from 'react'
import { Card as MCard } from '@cmsgov/ds-medicare-gov'
import classNames from 'classnames'
import { Pill } from 'components/Pill/Pill'
import { UpdatedStatus } from './UpdatedStatus'
import {
  Button,
  ButtonSiteActivityProps,
  ButtonSiteActivityPropsV2
} from 'components/Button/Button'
import { MyTooltip } from 'components/MyTooltip'
import { MyInternalLink, MyLink } from 'components/MyLink'
import { useTranslation } from 'react-i18next'
import { useReturnTrueForXSeconds } from 'hooks/useDisplayForXSeconds'
export interface IDropdownInfo {
  dropdownButtonProps?: {
    expandButtonText: string
    expandAriaLabel: string
    collapseButtonText: string
    collapseAriaLabel: string
    openSiteActivityProps?: ButtonSiteActivityProps | ButtonSiteActivityPropsV2
    closeSiteActivityProps?: ButtonSiteActivityProps | ButtonSiteActivityPropsV2
  }
  contentId: string
  content: React.ReactNode
}

export interface ICardProps {
  className?: string
  title?: string
  displayValue?: string
  truncatedValue?: string | ReactNode
  /**
   * When this prop value is updated, the 'updated' animation will trigger on the card
   */
  animationSubject?: unknown
  /**
   * When true, it won't skip the initial value for animationSubject
   */
  animateOnLoad?: boolean
  /**
   * When true, adjusts styling to fit as part of a larger group
   */
  isStacked?: boolean
  dropdown?: IDropdownInfo
  headingLevel?: '1' | '2' | '3' | '4' | '5' | '6'
  /** This will set `disabled` for button variant; it has no effect on link variant */
  disableEdit?: boolean
  toolTipText?: string
  buttonText: string
  editButtonAriaLabel?: string
  /** When this is undefined, the button will not be rendered */
  buttonCallbackOrHref: (() => void) | string | undefined
  buttonSiteActivityProps?: ButtonSiteActivityProps | ButtonSiteActivityPropsV2
  dataTestId?: string
}

// TODO - This should be renamed to something other than <Card> since this isn't a generic card and will be confused when we port over Cards from medicare.gov. Probably rename to "StatusCard"
export const Card: React.FC<ICardProps> = ({
  dataTestId,
  buttonCallbackOrHref,
  buttonText,
  toolTipText,
  title,
  displayValue,
  truncatedValue,
  animationSubject,
  animateOnLoad,
  isStacked,
  className,
  dropdown,
  headingLevel,
  disableEdit,
  buttonSiteActivityProps,
  editButtonAriaLabel
}) => {
  /*********
   * STATE *
   *********/
  const [isOpen, setIsOpen] = useState(Boolean(!dropdown?.dropdownButtonProps))
  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  const { t } = useTranslation('common')

  /*****************
   * UPDATED STATE *
   *****************/
  const displayUpdated = useReturnTrueForXSeconds({
    seconds: 5,
    initialValue: animateOnLoad
  }, [animationSubject, animateOnLoad])

  const showTruncated = truncatedValue && displayValue !== truncatedValue

  const evaluateButtonToRender = () => {
    if (buttonCallbackOrHref) {
      if (typeof buttonCallbackOrHref === 'string') {
        const isExternal = /^https?:\/\//.test(buttonCallbackOrHref)
        return isExternal
          ? (
          <MyLink
            className="ds-c-button"
            href={buttonCallbackOrHref}
            aria-label={editButtonAriaLabel}
            siteActivityProps={buttonSiteActivityProps}
          >
            {buttonText}
          </MyLink>
            )
          : (
          <MyInternalLink
            className="ds-c-button"
            data-testid={`${dataTestId}-btn-action`}
            to={buttonCallbackOrHref as string}
            aria-label={editButtonAriaLabel}
            siteActivityProps={buttonSiteActivityProps}
          >
            {buttonText}
          </MyInternalLink>
            )
      } else {
        return (
          <Button
            className="ds-c-button"
            data-testid={`${dataTestId}-btn-action`}
            onClick={buttonCallbackOrHref as () => void}
            siteActivityProps={buttonSiteActivityProps}
            aria-label={editButtonAriaLabel}
            disabled={disableEdit}
          >
            {buttonText}
          </Button>
        )
      }
    }
  }

  /**
   * @returns the dropdown open and close button, or nothing if the dropdown props are not defined
   */
  const renderDropdownOpenCloseButton = () => {
    return (
      dropdown?.dropdownButtonProps && (
        <Button
          data-testid="card-toggle-button"
          className="ds-c-button ds-c-button--ghost my-u-px--0 my-u-py--0"
          onClick={toggleOpen}
          aria-expanded={isOpen}
          aria-controls={dropdown.contentId}
          aria-label={
            isOpen
              ? dropdown?.dropdownButtonProps.collapseAriaLabel
              : dropdown?.dropdownButtonProps.expandAriaLabel
          }
          siteActivityProps={
            isOpen
              ? dropdown.dropdownButtonProps.closeSiteActivityProps
              : dropdown.dropdownButtonProps.openSiteActivityProps
          }
        >
          {isOpen
            ? dropdown?.dropdownButtonProps.collapseButtonText
            : dropdown?.dropdownButtonProps.expandButtonText}
        </Button>
      )
    )
  }

  // Type cast to prevent lint errors - there is probably a better typing for this
  const Heading = `h${headingLevel ?? 4}` as unknown as React.FC<{
    className: string
    children: React.ReactNode
  }>
  return (
    <div
      data-testid={dataTestId}
      className={classNames('my-c-card', {
        'my-c-card__updating': displayUpdated,
        'my-c-card-multiple__item': isStacked
      }, className)}
    >
      <MCard>
        <div className="my-u-py--7 my-u-px--12">
          <div className="ds-l-row ds-u-align-items--center">
            <div className="ds-l-md-col--4 ds-l-xs-col--12 ds-u-display--flex ds-u-justify-content--between my-u-py--4">
              <div>
                {toolTipText
                  ? (
                  <MyTooltip
                    className="ds-c-tooltip__trigger-link ds-u-text-align--left"
                    component="button"
                    maxWidth="300px"
                    // @ts-ignore
                    placement="bottom-start"
                    offset={[0, 10]}
                    data-testid="card_tooltip"
                    title={<div className="my-u-p--5">{toolTipText}</div>}
                  >
                    <Heading className="ds-text-heading--lg my-u-font-weight--600 my-u-m--0">
                      {title}
                    </Heading>
                  </MyTooltip>
                    )
                  : (
                  <Heading className="ds-text-heading--lg my-u-font-weight--600 my-u-m--0">
                    {title}
                  </Heading>
                    )}
                {renderDropdownOpenCloseButton()}
              </div>
              {/* Mobile view 'Updated' notification */}
              <div aria-hidden>
                <div className="ds-u-sm-display--none">
                  {displayUpdated && <UpdatedStatus showText />}
                </div>
              </div>
            </div>
            <div className="ds-l-md-col--8 ds-l-xs-col--12">
              <div className="ds-u-display--flex ds-u-justify-content--start ds-u-sm-justify-content--end ds-u-align-items--center">
                <div
                  aria-hidden
                  className={'ds-u-display--flex ds-u-align-items--center'}
                >
                  {/* Medium and up desktop view 'Updated' notification */}
                  <div className="ds-u-display--none ds-u-sm-display--inline-block ds-u-md-display--none my-u-mr--8">
                    {displayUpdated && <UpdatedStatus />}
                  </div>
                  {/* Small desktop view 'Updated' notification */}
                  <div className="ds-u-display--none ds-u-md-display--inline-block my-u-mr--8">
                    {displayUpdated && <UpdatedStatus showText />}
                  </div>
                </div>
                <div
                  aria-live="polite"
                  data-testid="aria-live"
                  className="ds-u-visibility--screen-reader"
                >
                  {displayUpdated && (
                    <span data-testid="aria-live-update">
                      {title} {t('cards.status.updated')}
                    </span>
                  )}
                </div>
                {displayValue && (
                  <div
                    className="my-u-mr--8 ds-u-display--flex ds-u-align-items--center ds-u-justify-content--end ds-u-overflow--hidden"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <Pill
                      className="my-u-sm-width--full"
                      aria-hidden={Boolean(showTruncated)}
                      title={showTruncated ? displayValue : undefined}
                    >
                      {showTruncated ? truncatedValue : displayValue}
                    </Pill>

                    {showTruncated && (
                      <div className="ds-u-visibility--screen-reader">
                        {displayValue}
                      </div>
                    )}
                  </div>
                )}
                {evaluateButtonToRender()}
              </div>
            </div>
          </div>
        </div>
        {dropdown && (
          <div
            className="ds-u-border-top--1 my-u-p--8"
            id={dropdown.contentId}
            hidden={!isOpen}
          >
            <div className="my-u-mx--4">{dropdown.content}</div>
          </div>
        )}
      </MCard>
    </div>
  )
}
